import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import ModalInfo from "../../../components/lib/modalInfo";
import { RiErrorWarningLine } from "react-icons/ri";
import { GrCircleQuestion } from "react-icons/gr";
import AssetsService from "../../../rest/assetsService";

const StudentRegisterForm = ({
    initialData = null,
    onSubmit,
    onDirty,
}) => {
    const [selectedImage, setSelectedImage] = useState(null);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        if (initialData?.image_url) {
            setSelectedImage(initialData.image_url);
        }
    }, [initialData]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            name: initialData?.name ?? "",
            email: initialData?.email ?? "",
            phone_number: initialData?.phone_number ?? "",
            emergency_value: initialData?.emergency_value ?? 0,
            spend_limit: initialData?.spend_limit ?? 0,
            isPayingStudent: initialData?.isPayingStudent ?? true,
            profileImage: null,
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .required("Nome é um campo obrigatório")
                .max(50, "O nome deve ter no máximo 50 caracteres")
                .test(
                    "two-words",
                    "O nome deve ser completo, formado por mais de uma palavra.",
                    value => value && value.trim().split(/\s+/).length >= 2
                ),
            email: Yup.string()
                .email("Email inválido")
                .matches(
                    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    "O email não pode conter acentos ou caracteres especiais inválidos"
                ),
            phone_number: Yup.string()
                .matches(/^[0-9]*$/, "O telefone deve conter apenas números (sem espaços ou símbolos)")
                .max(11, "O telefone deve ter no máximo 11 dígitos"),
            profileImage: Yup.mixed()
                .nullable()
                .test("fileSize", "A foto deve ter no máximo 25MB", (value) => {
                    if (!value) return true;
                    return value.size <= 25 * 1024 * 1024;
                })
                .test("fileType", "Formato de imagem inválido", (value) => {
                    if (!value) return true;
                    return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
                }),
        }),
        onSubmit: async (values) => {
            let imageUrl = initialData?.image_url || null;

            if (values.profileImage) {
                const formData = new FormData();
                formData.append("file", values.profileImage);
                const uploadResponse = await AssetsService.uploadStudentProfileImage(formData);
                imageUrl = uploadResponse.data;
            }

            onSubmit({
                ...values,
                image_url: imageUrl,
                role: "STUDENT",
                login_code: sessionStorage.getItem("loginCode"),
                client_code: sessionStorage.getItem("clientCode"),
                user_status: true,
                id: initialData?.id,
            });
        },
    });

    const handleImageChange = (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setSelectedImage(URL.createObjectURL(file));
        formik.setFieldValue("profileImage", file);
        onDirty();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const file = e.dataTransfer.files?.[0];
        if (!file) return;

        setSelectedImage(URL.createObjectURL(file));
        formik.setFieldValue("profileImage", file);
        onDirty();
    };

    const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
    const handleDragLeave = () => setIsDragging(false);

    return (
        <form
            data-step-form
            onSubmit={formik.handleSubmit}
            className="flex flex-col gap-4"
            onChange={onDirty}
        >
            <div className="w-full">
                <div className="mb-6">
                    <p className="mb-2 font-medium">Imagem do Aluno</p>

                    <div
                        className={`flex items-center justify-center rounded-md border-2 border-dashed p-4 transition
                        ${isDragging ? "ring-2 ring-custom_blue" : "border-gray-300"}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                    >
                        {selectedImage ? (
                            <div className="relative w-40 h-40">
                                <img
                                    src={selectedImage}
                                    alt="Preview"
                                    className="w-full h-full object-cover rounded-md"
                                />
                                <button
                                    type="button"
                                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                                    onClick={() => {
                                        setSelectedImage(null);
                                        formik.setFieldValue("profileImage", null);
                                        onDirty();
                                    }}
                                >
                                    ✕
                                </button>
                            </div>
                        ) : (
                            <label className="cursor-pointer text-center text-sm text-gray-500">
                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={handleImageChange}
                                />
                                Clique ou arraste a imagem aqui
                            </label>
                        )}
                    </div>
                    {formik.errors.profileImage && (
                        <p className="text-red-500 text-xs">{formik.errors.profileImage}</p>
                    )}
                </div>

                <label>
                    <div className="flex items-center justify-between">
                        <p>Nome do Aluno</p>
                        <ModalInfo icon={<RiErrorWarningLine />} content="Campo Obrigatório!" />
                    </div>
                    <input
                        className={`block w-full px-3 py-1.5 border rounded ${formik.touched.name && formik.errors.name ? "border-red-500" : ""}`}
                        name="name"
                        type="text"
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        value={formik.values.name}
                    />
                    {formik.touched.name && formik.errors.name && (
                        <p className="text-red-500 text-xs">{formik.errors.name}</p>
                    )}
                </label>

                <div className="grid grid-cols-2 gap-4 my-6">
                    <label>
                        <p>Email</p>
                        <input
                            className={`block w-full px-3 py-1.5 border rounded ${formik.touched.email && formik.errors.email ? "border-red-500" : ""}`}
                            name="email"
                            type="text"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.email}
                        />
                        {formik.touched.email && formik.errors.email && (
                            <p className="text-red-500 text-xs">{formik.errors.email}</p>
                        )}
                    </label>

                    <label>
                        <p>Telefone</p>
                        <input
                            className="block w-full px-3 py-1.5 border rounded"
                            name="phone_number"
                            type="text"
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={formik.values.phone_number}
                        />
                        {formik.touched.phone_number && formik.errors.phone_number && (
                            <p className="text-red-500 text-xs">{formik.errors.phone_number}</p>
                        )}
                    </label>
                </div>

                <div className="grid grid-cols-2 gap-4 my-6">
                    <label>
                        <div className="flex items-center justify-between">
                            <p>Valor de Emergência</p>
                            <ModalInfo icon={<GrCircleQuestion />} content="O valor de Emergência é o valor negativo que o aluno pode alcançar ficando em débito com a cantina." />
                        </div>
                        <input
                            className="block w-full px-3 py-1.5 border rounded"
                            name="emergency_value"
                            type="number"
                            step="0.05"
                            min="0"
                            onChange={formik.handleChange}
                            value={formik.values.emergency_value}
                        />
                    </label>

                    <label>
                        <div className="flex items-center justify-between">
                            <p>Limite de gastos diário</p>
                            <ModalInfo icon={<GrCircleQuestion />} content="O limite de gastos diário é o valor em pedidos que o aluno pode realizar por dia." />
                        </div>
                        <input
                            className="block w-full px-3 py-1.5 border rounded"
                            name="spend_limit"
                            type="number"
                            step="0.05"
                            min="0"
                            onChange={formik.handleChange}
                            value={formik.values.spend_limit}
                        />
                    </label>
                </div>

                <label className="flex items-center justify-center gap-4 mt-4">
                    Aluno Pagante:
                    <input
                        type="checkbox"
                        name="isPayingStudent"
                        checked={formik.values.isPayingStudent}
                        onChange={formik.handleChange}
                        defaultChecked="true"
                    />
                </label>
            </div>
        </form>
    );
};

export default StudentRegisterForm;
